import { SecondaryButton } from "@/components/buttons/SecondaryButton";
import AntIcon from "@/components/icons/AntIcon";
import { validationConstants, validationFunctions } from "@/utils/validationUtils";
import { App as AntApp, Button, Card, Space } from "antd";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useFilterVehicleModels } from "../../../../../common/api/queries";
import { PageSizes } from "../../../../../common/constants";
import { UUID } from "../../../../../typings/global";
import { VehicleMappingType } from "../../enums";
import { VehicleMappingData } from "../../types";
import VehicleModelCreateMappingModal from "./VehicleModelCreateMappingModal";
import VehicleModelMappingsListForm from "./VehicleModelMappingsListForm";

type Props = {
  selectedMappingTypes: VehicleMappingType[];
  onReturnsToBrand: () => void;
  selectedBrandId: UUID;
  mappingData: VehicleMappingData[];
};

export const VehicleModelMappings = ({
  selectedMappingTypes,
  onReturnsToBrand,
  selectedBrandId,
  mappingData,
}: Props) => {
  const { t } = useTranslation();
  const { message, modal } = AntApp.useApp();

  const [isModelCreateMappingModalOpen, setIsModelCreateMappingModalOpen] = useState<boolean>(false);
  const [keyword, setKeyword] = useState<string>();
  const [pageIndex, setPageIndex] = useState<number>(0);
  const [excludeMapped, setExcludeMapped] = useState<boolean>(false);
  const [updatedRowsIds, setUpdatedRowsIds] = useState<UUID[]>([]);

  const { data: modelsCurrentPage } = useFilterVehicleModels({
    id: selectedBrandId,
    object: {
      keyword,
      pageIndex,
      pageSize: PageSizes.SMALL,
      queriedMappings: selectedMappingTypes,
      excludeMapped,
    },
  });

  const handleSearchSubmit = (keyword: string) => {
    if (validationFunctions.validateSearchKeyword(keyword)) {
      checkUnsavedChangesBeforePageChange(() => setKeyword(keyword));
    } else {
      message.error(
        t("validation.size", {
          min: validationConstants.SEARCH_KEYWORD_MIN_LENGTH,
          max: validationConstants.SEARCH_KEYWORD_MAX_LENGTH,
        }),
      );
    }
  };

  const handleExcludeMappedChange = (excludeMapped: boolean) => {
    checkUnsavedChangesBeforePageChange(() => setExcludeMapped(excludeMapped));
  };

  const handleListPageChange = (pageNumber: number) => {
    checkUnsavedChangesBeforePageChange(() => setPageIndex(pageNumber - 1));
  };

  const checkUnsavedChangesBeforePageChange = (callback: () => void) => {
    if (updatedRowsIds.length > 0) {
      modal.confirm({
        title: t("common.unsavedChanges"),
        okText: t("common.continue"),
        cancelText: t("common.back"),
        onOk: callback,
      });
    } else {
      callback();
    }
  };

  return (
    <Card
      className="card-box"
      title={
        <h3>
          {modelsCurrentPage?.brand
            ? t("vehicle.titles.modelsMappingForBrand", { brandName: modelsCurrentPage.brand.name })
            : t("vehicle.titles.modelsMapping")}
        </h3>
      }
      extra={
        <Space className="margin-top-tiny margin-bottom-tiny">
          <Button icon={<AntIcon type="double-left" />} onClick={onReturnsToBrand}>
            {t("vehicle.actions.backToBrands")}
          </Button>

          <SecondaryButton icon={<AntIcon type="plus" />} onClick={() => setIsModelCreateMappingModalOpen(true)}>
            {t("vehicle.actions.createModel")}
          </SecondaryButton>
        </Space>
      }
    >
      <VehicleModelMappingsListForm
        selectedBrandId={selectedBrandId}
        selectedMappingTypes={selectedMappingTypes}
        mappingData={mappingData}
        currentPage={modelsCurrentPage}
        onExcludeMappedChange={handleExcludeMappedChange}
        onListPageChange={handleListPageChange}
        onSearchSubmit={handleSearchSubmit}
        updatedRowsIds={updatedRowsIds}
        setUpdatedRowsIds={setUpdatedRowsIds}
      />

      <VehicleModelCreateMappingModal
        open={isModelCreateMappingModalOpen}
        selectedBrand={modelsCurrentPage?.brand}
        selectedMappingTypes={selectedMappingTypes}
        mappingData={mappingData}
        onFormCancel={() => setIsModelCreateMappingModalOpen(false)}
      />
    </Card>
  );
};
